import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Sparkles, Compass, GraduationCap, Trophy, BookOpen, Mic, Brain,
  Rocket, Users, Award, DollarSign, Target, Volume2, Home, Star, BarChart3, ChevronRight, Check, Plus, Lock,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { speak, stopSpeak } from './demo/demoLang';

/* -------------------------------------------------------
   OuiStuPaStu SUPER APP — Phase 1 shell
   Apple/Google-style: one clear purpose per screen.
------------------------------------------------------- */

const SECTIONS = [
  { key: 'home',       label: 'Home',        icon: Home },
  { key: 'career',     label: 'Career',      icon: Compass },
  { key: 'curriculum', label: 'Curriculum',  icon: GraduationCap },
  { key: 'exams',      label: 'Exam prep',   icon: Trophy },
  { key: 'personas',   label: 'Mentors',     icon: Brain },
  { key: 'dictation',  label: 'Dictation',   icon: Mic },
  { key: 'lifeskills', label: 'Life skills', icon: DollarSign },
  { key: 'leadership', label: 'Leadership',  icon: Award },
  { key: 'tourneys',   label: 'Tournaments', icon: Trophy },
  { key: 'community',  label: 'Study dates', icon: Users },
  { key: 'tiers',      label: 'Rewards',     icon: Star },
  { key: 'pricing',    label: 'Pricing',     icon: Target },
];

const Shell = ({ children }) => (
  <div className="min-h-screen bg-white text-slate-900" data-testid="super-shell">{children}</div>
);
const H1 = ({ children }) => <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{children}</h1>;
const H2 = ({ children }) => <h2 className="font-heading text-xl font-bold text-slate-900">{children}</h2>;
const Sub = ({ children }) => <p className="text-slate-500 text-sm mt-1">{children}</p>;
const Card = ({ children, className = '', ...rest }) => (
  <div {...rest} className={`bg-white rounded-3xl border border-slate-200 p-5 hover:border-slate-300 transition-colors ${className}`}>{children}</div>
);
const Pill = ({ children, tone = 'slate' }) => {
  const tones = { slate: 'bg-slate-100 text-slate-700', teal: 'bg-teal/10 text-teal', gold: 'bg-gold/25 text-navy' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-heading font-bold uppercase tracking-wider ${tones[tone] || tones.slate}`}>{children}</span>;
};

/* ---------- HOME ---------- */
const HomeSection = ({ go }) => {
  const { user } = useAuth();
  return (
    <div className="space-y-6" data-testid="section-home">
      <div>
        <H1>Hi {user?.name?.split(' ')[0] || 'friend'}.</H1>
        <Sub>Learn a lot. Reclaim time for sports and life. Grow into a leader.</Sub>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card onClick={() => go('personas')} className="cursor-pointer" data-testid="home-cta-tutor">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-slate-900 text-white flex items-center justify-center"><Brain className="w-5 h-5" /></div>
            <div className="min-w-0"><div className="font-heading font-bold">Ask a mentor</div><div className="text-slate-500 text-sm">15 personas · Socratic style</div></div>
            <ChevronRight className="ml-auto w-4 h-4 text-slate-400" />
          </div>
        </Card>
        <Card onClick={() => go('career')} className="cursor-pointer" data-testid="home-cta-career">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-teal text-white flex items-center justify-center"><Compass className="w-5 h-5" /></div>
            <div className="min-w-0"><div className="font-heading font-bold">Your career path</div><div className="text-slate-500 text-sm">Every lesson tied to your future</div></div>
            <ChevronRight className="ml-auto w-4 h-4 text-slate-400" />
          </div>
        </Card>
        <Card onClick={() => go('exams')} className="cursor-pointer" data-testid="home-cta-exams">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-amber-500 text-white flex items-center justify-center"><Trophy className="w-5 h-5" /></div>
            <div className="min-w-0"><div className="font-heading font-bold">Exam prep</div><div className="text-slate-500 text-sm">TOEFL · SAT · TestDaF · BAC · JEE …</div></div>
            <ChevronRight className="ml-auto w-4 h-4 text-slate-400" />
          </div>
        </Card>
        <Card onClick={() => go('leadership')} className="cursor-pointer" data-testid="home-cta-leadership">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-rose-600 text-white flex items-center justify-center"><Award className="w-5 h-5" /></div>
            <div className="min-w-0"><div className="font-heading font-bold">Leadership class</div><div className="text-slate-500 text-sm">One brave act. One journal entry.</div></div>
            <ChevronRight className="ml-auto w-4 h-4 text-slate-400" />
          </div>
        </Card>
      </div>
      <Card>
        <H2>Today's principle</H2>
        <Sub>Respect wins. Effort compounds. Curiosity is the whole game.</Sub>
      </Card>
    </div>
  );
};

/* ---------- CAREER ---------- */
const CareerSection = () => {
  const [data, setData] = useState(null);
  const [customName, setCustomName] = useState('');
  const load = async () => setData((await api.get('/super/careers')).data);
  useEffect(() => { load(); }, []);
  const choose = async (id, custom_name = null) => {
    try { await api.post('/super/career', { career_id: id, custom_name }); toast.success('Career path set'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-5" data-testid="section-career">
      <div><H1>Choose your path.</H1><Sub>Every topic will connect back to it. You can change this at any time.</Sub></div>
      {data?.chosen && (
        <Card data-testid="career-chosen">
          <div className="flex items-center gap-3">
            <div className="text-4xl">{data.chosen.emoji}</div>
            <div><div className="text-slate-500 text-[11px] font-bold uppercase">Currently pursuing</div><div className="font-heading text-xl font-bold">{data.chosen.name}</div></div>
          </div>
          <div className="mt-3 text-slate-700 text-sm">{data.chosen.why}</div>
          <div className="mt-2 flex flex-wrap gap-1.5">{(data.chosen.subjects || []).map((s) => <Pill key={s} tone="teal">{s}</Pill>)}</div>
        </Card>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="career-grid">
        {(data?.careers || []).filter((c) => c.id !== 'custom').map((c) => {
          const active = data?.chosen?.id === c.id;
          return (
            <button key={c.id} onClick={() => choose(c.id)}
              className={`p-4 rounded-3xl text-left transition-colors ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border border-slate-200 hover:border-slate-400'} border`}
              data-testid={`career-${c.id}`}>
              <div className="text-3xl">{c.emoji}</div>
              <div className="font-heading font-bold mt-2 text-sm leading-tight">{c.name}</div>
              <div className={`text-[11px] mt-1 leading-tight ${active ? 'text-white/70' : 'text-slate-500'}`}>{(c.subjects || []).slice(0, 2).join(' · ')}</div>
            </button>
          );
        })}
      </div>
      <Card data-testid="career-custom">
        <div className="text-slate-500 text-[11px] font-bold uppercase mb-2">Or write your own</div>
        <div className="flex gap-2">
          <input value={customName} onChange={(e) => setCustomName(e.target.value)} maxLength={60}
            className="flex-1 h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-slate-900 focus:outline-none font-body text-sm"
            placeholder="e.g. Marine biologist" data-testid="career-custom-input" />
          <button onClick={() => customName.trim() && choose('custom', customName.trim())}
            className="h-12 px-4 rounded-xl bg-slate-900 text-white font-heading font-bold text-sm" data-testid="career-custom-save">Set</button>
        </div>
      </Card>
    </div>
  );
};

/* ---------- CURRICULUM ---------- */
const CurriculumSection = () => {
  const [data, setData] = useState(null);
  const load = async () => setData((await api.get('/super/schools')).data);
  useEffect(() => { load(); }, []);
  const choose = async (school_id, grade = null) => {
    try { await api.post('/super/school', { school_id, grade }); toast.success('School set'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-5" data-testid="section-curriculum">
      <div><H1>Your school. Your curriculum.</H1><Sub>Default to where you're actually enrolled — not where you're travelling.</Sub></div>
      {data?.chosen && (
        <Card data-testid="curriculum-chosen">
          <div className="text-slate-500 text-[11px] font-bold uppercase">Currently learning at</div>
          <div className="font-heading text-xl font-bold">{data.chosen.name}</div>
          <div className="text-slate-500 text-sm">{data.chosen.system} · {data.chosen.country}{data.chosen.grade ? ` · Grade ${data.chosen.grade}` : ''}</div>
          <div className="mt-3">
            <Pill tone="gold">Champions League: monthly class-vs-class</Pill>
          </div>
        </Card>
      )}
      <div className="space-y-2" data-testid="school-list">
        {(data?.schools || []).map((s) => {
          const active = data?.chosen?.id === s.id;
          return (
            <button key={s.id} onClick={() => choose(s.id)}
              className={`w-full text-left p-4 rounded-2xl border transition-colors ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 hover:border-slate-400'}`}
              data-testid={`school-${s.id}`}>
              <div className="font-heading font-bold">{s.name}</div>
              <div className={`text-sm ${active ? 'text-white/70' : 'text-slate-500'}`}>{s.country} · {s.region} · {s.system}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ---------- EXAM PREP ---------- */
const ExamsSection = () => {
  const [data, setData] = useState(null);
  const [region, setRegion] = useState('All');
  useEffect(() => { api.get('/super/exams').then(({ data }) => setData(data)); }, []);
  const shown = useMemo(() => (data?.exams || []).filter((e) => region === 'All' || e.region === region), [data, region]);
  return (
    <div className="space-y-5" data-testid="section-exams">
      <div><H1>Exam prep library.</H1><Sub>Global. Everything from BAC to Gaokao.</Sub></div>
      <div className="flex flex-wrap gap-2" data-testid="exam-regions">
        {['All', ...(data?.regions || [])].map((r) => (
          <button key={r} onClick={() => setRegion(r)}
            className={`h-9 px-3 rounded-full font-heading font-bold text-xs ${region === r ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            data-testid={`region-${r}`}>{r}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="exam-list">
        {shown.map((e) => (
          <Card key={e.id} data-testid={`exam-${e.id}`}>
            <div className="flex items-start gap-3">
              <div className="text-3xl">{e.flag}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-heading font-bold text-lg">{e.name}</div>
                  <Pill>{e.region}</Pill>
                </div>
                <div className="text-slate-500 text-sm mt-0.5">{e.for}</div>
                <div className="mt-2 flex flex-wrap gap-1.5">{e.areas.map((a) => <Pill key={a} tone="teal">{a}</Pill>)}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

/* ---------- MENTOR PERSONAS ---------- */
const PersonaSection = () => {
  const [data, setData] = useState(null);
  const [family, setFamily] = useState({ name: '', relation: '' });
  const [speaking, setSpeaking] = useState(null);
  useEffect(() => { api.get('/super/personas').then(({ data }) => setData(data)); }, []);
  const speakOne = (p) => {
    if (speaking === p.id) { stopSpeak(); setSpeaking(null); return; }
    speak(p.signature, 'en-US'); setSpeaking(p.id); setTimeout(() => setSpeaking((s) => s === p.id ? null : s), 6000);
  };
  const setFamPersona = async () => {
    if (!family.name.trim()) return;
    await api.post('/super/family-persona', family); toast.success('Family mentor added');
    setData((d) => ({ ...d, family: { ...family, id: 'family-' + family.name.toLowerCase().replace(/\s+/g, '-').slice(0, 20) } }));
  };
  return (
    <div className="space-y-5" data-testid="section-personas">
      <div><H1>Mentors.</H1><Sub>Learn from history's greats. Or from someone in your own family.</Sub></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="persona-list">
        {(data?.personas || []).map((p) => (
          <Card key={p.id} className="!p-4" data-testid={`persona-${p.id}`}>
            <div className="text-3xl">{p.emoji}</div>
            <div className="font-heading font-bold text-sm mt-2 leading-tight">{p.name}</div>
            <div className="text-slate-500 text-[11px] mt-0.5 leading-tight">{p.subject}</div>
            <button onClick={() => speakOne(p)} className="mt-2 inline-flex items-center gap-1 text-teal text-[11px] font-bold" data-testid={`persona-speak-${p.id}`}>
              <Volume2 className="w-3 h-3" /> {speaking === p.id ? 'Stop' : 'Hear'}
            </button>
          </Card>
        ))}
      </div>
      <Card data-testid="family-persona">
        <div className="text-slate-500 text-[11px] font-bold uppercase mb-2">Family mentor</div>
        <div className="text-slate-700 text-sm">Enter a family member's name — the tutor can speak as them.</div>
        <div className="flex flex-wrap gap-2 mt-2">
          <input value={family.name} onChange={(e) => setFamily({ ...family, name: e.target.value })} placeholder="Name (e.g. Grandpa Jean)"
            className="flex-1 min-w-[140px] h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-slate-900 focus:outline-none font-body text-sm"
            data-testid="family-name" />
          <input value={family.relation} onChange={(e) => setFamily({ ...family, relation: e.target.value })} placeholder="Relation (optional)"
            className="flex-1 min-w-[120px] h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-slate-900 focus:outline-none font-body text-sm"
            data-testid="family-relation" />
          <button onClick={setFamPersona} className="h-12 px-4 rounded-xl bg-slate-900 text-white font-heading font-bold text-sm" data-testid="family-save">Set</button>
        </div>
        {data?.family && <div className="text-teal text-xs font-bold mt-2">Active: {data.family.name}{data.family.relation ? ` · ${data.family.relation}` : ''}</div>}
      </Card>
    </div>
  );
};

/* ---------- DICTATION ---------- */
const DictationSection = () => {
  const [lang, setLang] = useState('en');
  const [level, setLevel] = useState('medium');
  const [target, setTarget] = useState('');
  const [typed, setTyped] = useState('');
  const [result, setResult] = useState(null);
  const load = async () => { const { data } = await api.get(`/super/dictation?lang=${lang}&level=${level}`); setTarget(data.text); setTyped(''); setResult(null); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [lang, level]);
  const play = () => speak(target, { en: 'en-US', fr: 'fr-FR', de: 'de-DE' }[lang] || 'en-US');
  const check = () => {
    const norm = (s) => s.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, '').trim().split(/\s+/);
    const a = norm(target), b = norm(typed);
    let hit = 0; a.forEach((w, i) => { if (b[i] === w) hit++; });
    setResult({ score: Math.round((hit / a.length) * 100), total: a.length });
  };
  return (
    <div className="space-y-5" data-testid="section-dictation">
      <div><H1>Dictation.</H1><Sub>Listen. Type. Get instant feedback.</Sub></div>
      <div className="flex flex-wrap gap-2">
        {[['en','English'],['fr','Français'],['de','Deutsch']].map(([k, l]) => (
          <button key={k} onClick={() => setLang(k)} className={`h-9 px-3 rounded-full font-heading font-bold text-xs ${lang === k ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`} data-testid={`dictation-lang-${k}`}>{l}</button>
        ))}
        {['easy','medium','hard'].map((v) => (
          <button key={v} onClick={() => setLevel(v)} className={`h-9 px-3 rounded-full font-heading font-bold text-xs capitalize ${level === v ? 'bg-teal text-white' : 'bg-slate-100 text-slate-700'}`} data-testid={`dictation-level-${v}`}>{v}</button>
        ))}
      </div>
      <Card>
        <div className="flex gap-2 mb-3">
          <button onClick={play} className="h-11 px-4 rounded-xl bg-slate-900 text-white font-heading font-bold text-sm inline-flex items-center gap-2" data-testid="dictation-play"><Volume2 className="w-4 h-4" /> Play</button>
          <button onClick={load} className="h-11 px-4 rounded-xl bg-slate-100 text-slate-700 font-heading font-bold text-sm" data-testid="dictation-new">New passage</button>
        </div>
        <textarea value={typed} onChange={(e) => setTyped(e.target.value)} rows={4} placeholder="Type what you hear…"
          className="w-full rounded-2xl border-2 border-slate-200 focus:border-slate-900 focus:outline-none p-3 font-body" data-testid="dictation-input" />
        <button onClick={check} disabled={!typed.trim()} className="mt-3 h-11 px-5 rounded-full bg-gold text-navy font-heading font-bold shadow-gold disabled:opacity-50" data-testid="dictation-check">Check</button>
        {result && <div className="mt-3 rounded-2xl bg-slate-50 p-3 text-slate-800 text-sm" data-testid="dictation-result">Score: <b>{result.score}%</b> ({result.total} words). Target text was: <em className="text-slate-500">"{target}"</em></div>}
      </Card>
    </div>
  );
};

/* ---------- LIFE SKILLS ---------- */
const LifeSkillsSection = () => {
  const [skills, setSkills] = useState([]);
  useEffect(() => { api.get('/super/life-skills').then(({ data }) => setSkills(data.skills || [])); }, []);
  return (
    <div className="space-y-5" data-testid="section-lifeskills">
      <div><H1>Life skills.</H1><Sub>Taxes, real estate, starting a company, raising money. In plain words.</Sub></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {skills.map((s) => (
          <Card key={s.id} data-testid={`life-${s.id}`}>
            <div className="flex items-start gap-3">
              <div className="text-3xl">{s.flag}</div>
              <div className="min-w-0 flex-1">
                <div className="font-heading font-bold leading-tight">{s.topic}</div>
                <div className="text-slate-500 text-[11px] mt-0.5">{s.duration_min} min read</div>
                <div className="text-slate-700 text-sm mt-1">{s.summary}</div>
                <ul className="mt-2 space-y-1">{s.bullets.map((b, i) => <li key={i} className="text-slate-800 text-sm flex items-start gap-2"><Check className="w-3.5 h-3.5 text-teal shrink-0 mt-0.5" />{b}</li>)}</ul>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

/* ---------- LEADERSHIP ---------- */
const LeadershipSection = () => {
  const [challenges, setChallenges] = useState([]);
  const [entries, setEntries] = useState([]);
  const [text, setText] = useState('');
  const [selected, setSelected] = useState(null);
  const load = async () => {
    const c = (await api.get('/super/leadership/challenges')).data.challenges;
    const j = (await api.get('/super/leadership/journal')).data.entries;
    setChallenges(c); setEntries(j);
  };
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!text.trim()) return;
    await api.post('/super/leadership/journal', { challenge_id: selected, text });
    toast.success('Journal entry saved · +10 effort'); setText(''); setSelected(null); await load();
  };
  return (
    <div className="space-y-5" data-testid="section-leadership">
      <div><H1>Leadership class.</H1><Sub>Do one uncomfortable thing today. Write one honest sentence about it.</Sub></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="lead-challenges">
        {challenges.map((c) => (
          <button key={c.id} onClick={() => setSelected(c.id)}
            className={`p-4 rounded-2xl border text-left transition-colors ${selected === c.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200 hover:border-slate-400'}`}
            data-testid={`lead-challenge-${c.id}`}>
            <div className="text-2xl">{c.emoji}</div>
            <div className="font-heading font-bold text-sm mt-2">{c.title}</div>
            <div className={`text-[11px] mt-0.5 capitalize ${selected === c.id ? 'text-white/70' : 'text-slate-500'}`}>{c.level}</div>
          </button>
        ))}
      </div>
      <Card data-testid="lead-journal-write">
        <div className="text-slate-500 text-[11px] font-bold uppercase mb-2">Journal</div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="What did you attempt? How did it feel?"
          className="w-full rounded-2xl border-2 border-slate-200 focus:border-slate-900 focus:outline-none p-3 font-body" data-testid="lead-journal-input" />
        <button onClick={submit} disabled={!text.trim()} className="mt-3 h-11 px-5 rounded-full bg-gold text-navy font-heading font-bold shadow-gold disabled:opacity-50" data-testid="lead-journal-save">Save entry (+10)</button>
      </Card>
      {entries.length > 0 && (
        <div className="space-y-2" data-testid="lead-entries">
          <div className="text-slate-500 text-[11px] font-bold uppercase">Previous entries</div>
          {entries.slice(0, 6).map((e) => (
            <Card key={e.id} className="!p-3" data-testid={`lead-entry-${e.id}`}>
              <div className="text-slate-500 text-[11px]">{new Date(e.created_at).toLocaleDateString()}{e.challenge_id ? ` · ${e.challenge_id}` : ''}</div>
              <div className="text-slate-800 text-sm mt-1">{e.text}</div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------- TOURNAMENTS ---------- */
const TournamentSection = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/super/tournaments').then(({ data }) => setData(data)); }, []);
  return (
    <div className="space-y-5" data-testid="section-tourneys">
      <div><H1>Tournaments.</H1><Sub>Champions League every month. World Cup every four years. Opt in when you're ready.</Sub></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="tourney-list">
        {(data?.tournaments || []).map((t) => (
          <Card key={t.id} data-testid={`tourney-${t.id}`}>
            <div className="flex items-center gap-3">
              <div className="text-4xl">{t.flag}</div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold leading-tight">{t.title}</div>
                <div className="text-slate-500 text-[11px] mt-0.5">Starts {t.starts_on} · {t.tier}</div>
              </div>
              {t.kind === 'world' && <Pill tone="gold">FLAGSHIP</Pill>}
            </div>
            <button className="mt-3 h-10 px-4 rounded-full bg-slate-900 text-white font-heading font-bold text-sm" data-testid={`tourney-optin-${t.id}`} onClick={() => toast.success(`You've opted into ${t.title}`)}>Opt in</button>
          </Card>
        ))}
      </div>
      <Card data-testid="tourney-leaderboard">
        <div className="text-slate-500 text-[11px] font-bold uppercase mb-2">Sample regional leaderboard</div>
        <ul className="space-y-1.5">
          {(data?.sample_leaderboard || []).map((r) => (
            <li key={r.rank} className={`flex items-center gap-3 p-2 rounded-xl ${r.is_me ? 'bg-gold/25' : 'bg-slate-50'}`}>
              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center font-heading font-bold text-sm">{r.rank}</div>
              <div className="flex-1 min-w-0"><div className="font-heading font-bold text-sm truncate">{r.name}</div><div className="text-slate-500 text-[11px]">{r.school}</div></div>
              <div className="font-heading font-bold text-sm">{r.points}</div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
};

/* ---------- COMMUNITY ---------- */
const CommunitySection = () => {
  const [dates, setDates] = useState([]);
  const [form, setForm] = useState({ subject: 'Math', when: '', role: 'study_group', note: '' });
  const load = async () => setDates((await api.get('/super/study-dates')).data.dates || []);
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.when) { toast.error('Pick a date'); return; }
    await api.post('/super/study-dates', form); toast.success('Study date scheduled'); setForm({ ...form, when: '', note: '' }); await load();
  };
  return (
    <div className="space-y-5" data-testid="section-community">
      <div><H1>Study dates.</H1><Sub>Two brains beat one. Pair up on a subject — or invite 2-6 friends.</Sub></div>
      <Card>
        <div className="grid grid-cols-2 gap-2">
          <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="h-12 px-3 rounded-xl border-2 border-slate-200" data-testid="study-subject">
            {['Math','Physics','Chemistry','Biology','English','Français','Deutsch','History','Geography','ICT'].map((s) => <option key={s}>{s}</option>)}
          </select>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="h-12 px-3 rounded-xl border-2 border-slate-200" data-testid="study-role">
            <option value="need_help">I need help</option>
            <option value="help">I can help someone</option>
            <option value="study_group">Study group (2-6)</option>
          </select>
          <input type="datetime-local" value={form.when} onChange={(e) => setForm({ ...form, when: e.target.value })} className="h-12 px-3 rounded-xl border-2 border-slate-200 col-span-2" data-testid="study-when" />
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note (optional)" className="h-12 px-3 rounded-xl border-2 border-slate-200 col-span-2" data-testid="study-note" />
        </div>
        <button onClick={add} className="mt-3 h-11 px-5 rounded-full bg-gold text-navy font-heading font-bold shadow-gold inline-flex items-center gap-1.5" data-testid="study-add"><Plus className="w-4 h-4" /> Schedule</button>
      </Card>
      {dates.length > 0 && (
        <div className="space-y-2" data-testid="study-list">
          {dates.map((d) => (
            <Card key={d.id} className="!p-3" data-testid={`study-${d.id}`}>
              <div className="font-heading font-bold text-sm">{d.subject} · {new Date(d.when).toLocaleString()}</div>
              <div className="text-slate-500 text-[11px]">{d.role.replace('_', ' ')}{d.note ? ` · ${d.note}` : ''}</div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------- REWARDS TIERS ---------- */
const TiersSection = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/super/tiers').then(({ data }) => setData(data)); }, []);
  return (
    <div className="space-y-5" data-testid="section-tiers">
      <div><H1>Your journey.</H1><Sub>Explorer → Nobel. Points come from showing up.</Sub></div>
      <Card data-testid="tier-current">
        <div className="text-slate-500 text-[11px] font-bold uppercase">You're a</div>
        <div className="font-heading text-2xl font-bold flex items-center gap-2">
          <span>{data?.current?.emoji}</span>{data?.current?.name}
        </div>
        <div className="text-slate-700 text-sm mt-1">{data?.current?.perk}</div>
        {data?.next && <div className="text-slate-500 text-xs mt-2">Next: <b>{data.next.name}</b> at {data.next.min_points} pts · you have {data.total_points}.</div>}
      </Card>
      <div className="space-y-2" data-testid="tier-ladder">
        {(data?.tiers || []).map((t) => {
          const reached = data?.total_points >= t.min_points;
          return (
            <Card key={t.id} className={`!p-3 ${reached ? '' : 'opacity-70'}`} data-testid={`tier-${t.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-2xl" style={{ background: t.color + '22' }}>{t.emoji}</div>
                <div className="flex-1"><div className="font-heading font-bold">{t.name}</div><div className="text-slate-500 text-xs">{t.perk}</div></div>
                <div className="text-slate-500 text-xs font-bold inline-flex items-center gap-1">{reached ? <Check className="w-3.5 h-3.5 text-teal" /> : <Lock className="w-3.5 h-3.5" />}{t.min_points} pts</div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

/* ---------- PRICING ---------- */
const PricingSection = () => (
  <div className="space-y-5" data-testid="section-pricing">
    <div><H1>Pricing.</H1><Sub>Flat annual family subscription. Voice credits (Phase 2) are pay-as-you-go.</Sub></div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <Card data-testid="price-family"><Pill tone="gold">Family</Pill><div className="font-heading text-3xl font-bold mt-2">$149<span className="text-slate-500 text-sm">/year</span></div><ul className="mt-3 space-y-1 text-sm text-slate-700"><li>✓ Unlimited text tutor</li><li>✓ Unlimited quizzes</li><li>✓ All curricula & exam prep</li><li>✓ Up to 5 children</li></ul></Card>
      <Card data-testid="price-voice"><Pill tone="teal">Voice credits</Pill><div className="font-heading text-3xl font-bold mt-2">$0.05<span className="text-slate-500 text-sm">/min</span></div><ul className="mt-3 space-y-1 text-sm text-slate-700"><li>Phase 2 · pay-as-you-go</li><li>ElevenLabs-grade voices</li><li>Auto-summary after each lecture</li></ul></Card>
      <Card data-testid="price-school"><Pill>School licence</Pill><div className="font-heading text-3xl font-bold mt-2">$3,000<span className="text-slate-500 text-sm">/year</span></div><ul className="mt-3 space-y-1 text-sm text-slate-700"><li>Whole-school access</li><li>Teacher dashboards</li><li>Class-vs-class Champions League</li></ul></Card>
    </div>
  </div>
);

/* ---------- SHELL ---------- */
export default function SuperApp() {
  const [tab, setTab] = useState('home');
  const active = SECTIONS.find((s) => s.key === tab);
  return (
    <Shell>
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/dashboard" className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid="super-back" aria-label="Back"><ArrowLeft className="w-4 h-4 text-slate-700" /></Link>
          <div className="min-w-0 flex-1">
            <div className="font-heading text-sm font-bold tracking-tight">OuiStuPaStu Super App</div>
            <div className="text-slate-500 text-[10px]">One clear purpose per screen.</div>
          </div>
          <Sparkles className="w-4 h-4 text-slate-400" />
        </div>
        <nav className="max-w-4xl mx-auto overflow-x-auto px-2 pb-1" data-testid="super-nav">
          <div className="flex min-w-max">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const on = tab === s.key;
              return (
                <button key={s.key} onClick={() => setTab(s.key)}
                  className={`px-3 py-2 flex items-center gap-1.5 border-b-2 whitespace-nowrap font-heading font-bold text-xs transition-colors ${on ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
                  data-testid={`super-tab-${s.key}`}>
                  <Icon className="w-3.5 h-3.5" /> {s.label}
                </button>
              );
            })}
          </div>
        </nav>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-6">
        {tab === 'home'       && <HomeSection go={setTab} />}
        {tab === 'career'     && <CareerSection />}
        {tab === 'curriculum' && <CurriculumSection />}
        {tab === 'exams'      && <ExamsSection />}
        {tab === 'personas'   && <PersonaSection />}
        {tab === 'dictation'  && <DictationSection />}
        {tab === 'lifeskills' && <LifeSkillsSection />}
        {tab === 'leadership' && <LeadershipSection />}
        {tab === 'tourneys'   && <TournamentSection />}
        {tab === 'community'  && <CommunitySection />}
        {tab === 'tiers'      && <TiersSection />}
        {tab === 'pricing'    && <PricingSection />}
      </main>
    </Shell>
  );
}
